"use client";

import { useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import { ImageDecodePanel } from "@/components/tools/latex-dev/image-decode";
import { ImageEncodePanel } from "@/components/tools/latex-dev/image-encode";
import { useToolUi } from "@/components/tools/tool-ui";

/** A 16 × 16 palette PNG, 140 bytes: small enough to show the overhead of Base64 honestly. */
export const EXAMPLE_PNG_BASE64 =
  "iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAADFBMVEUdTtg4vfj4+vwPFyoCvIM4AAAAO0lEQVR42qWMQQ4AIAjDCv9/tFMJkl3dhbQZAyWU/5vKZZozD/J4G9HutaiZFr07RM06B87j4h7v4X8Lbm8A0JXdVegAAAAASUVORK5CYII=";

/** English strings. A translated content file overrides these through `ui`. */
export const IMAGE_TO_BASE64_STRINGS = {
  modeLabel: "Direction",
  modes: {
    encode: "Image → Base64",
    decode: "Base64 → image",
  },
  chooseLabel: "Choose an image file",
  dropHint:
    "or drop one onto this box. The file is read in your browser and never uploaded.",
  loadExample: "Use a small example image",
  clear: "Clear",
  formatLabel: "Output",
  formats: {
    dataUri: "Data URI",
    base64: "Base64 only",
    html: "HTML img tag",
    css: "CSS background-image",
    markdown: "Markdown image",
    svgText: "SVG as URL-encoded text (no Base64)",
  },
  altLabel: "Alt text (for the HTML and Markdown outputs)",
  altHint:
    "Describe the image for people who cannot see it. Leave it empty only if the image is purely decorative.",
  reading: "Reading the file…",
  resultsHeading: "Result",
  previewAlt: "Preview of the image",
  typeLabel: "Type",
  fileSize: "File size",
  base64Length: "Base64 characters",
  dataUriLength: "Data URI characters",
  overhead: "Data URI is longer than the file by",
  dimensions: "Dimensions",
  dimensionsValue: "{width} × {height} px",
  unknownDimensions: "—",
  outputLabel: "Output text",
  copy: "Copy",
  copied: "Copied!",
  downloadText: "Download as .txt",
  filenameText: "image-base64.txt",
  truncated:
    "Showing the first {shown} of {total} characters. Copy and Download use all of it.",
  sizeAdvice:
    "Rule of thumb: inlining saves a request but adds about a third to the size, and the image can no longer be cached by itself. It usually pays for images of a few kilobytes, and rarely for larger ones.",
  largeWarning:
    "A data URI of this size ({size}) is heavy for a page. Linking to the image file is usually better.",
  svgNote:
    "This is an SVG. Writing it as URL-encoded text is usually shorter than Base64, and keeps it readable.",
  cannotDraw:
    "The browser cannot draw this file as an image, so there is no preview or size. The Base64 is still correct for the file's bytes.",
  encodeErrors: {
    tooBig:
      "That file is {size}. The limit is {limit}; larger files make the page struggle.",
    notImage: "That file is not an image ({type}).",
    readFailed: "The browser could not read that file.",
  },
  emptyEncode: "Choose an image to convert it.",
  decodeLabel: "Data URI, HTML or CSS snippet, or bare Base64",
  decodePlaceholder: "data:image/png;base64,iVBORw0KGgo…",
  decodeHint:
    "Paste a data URI, an img tag or CSS that contains one, or just the Base64. Decoded in your browser; nothing is uploaded.",
  decodeExampleLabel: "Load the example",
  emptyDecode: "Paste some Base64 or a data URI to see the image.",
  decodeErrors: {
    empty: "There is nothing to decode.",
    noData: "The data URI has nothing after the comma.",
    badBase64:
      "This is not valid Base64 or a data URI. Base64 uses letters, digits, + and /, and may end with = padding.",
  },
  sources: {
    dataUri: "a data URI",
    html: "an img tag",
    css: "a CSS url()",
    raw: "bare Base64",
  },
  detectedType: "Detected type",
  declaredType: "Type in the data URI",
  foundIn: "Found in",
  none: "—",
  downloadImage: "Download the image",
  copyDataUri: "Copy as a data URI",
  decodeWarnings: {
    mismatch:
      "The data URI says {declared}, but the bytes look like {actual}. The bytes are used.",
    notImage:
      "This is not an image ({type}). It can be downloaded but not previewed.",
    unknownType: "The bytes do not match a known image format.",
    noPrefix: "No data: prefix was found, so the type comes from the bytes.",
  },
  warningsHeading: "Worth knowing",
} as const;

export type ImageStrings = typeof IMAGE_TO_BASE64_STRINGS;

type Mode = "encode" | "decode";

export default function ImageToBase64Widget() {
  const strings = useToolUi(IMAGE_TO_BASE64_STRINGS);
  const [mode, setMode] = useState<Mode>("encode");
  return (
    <section className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="itb-mode"
        onChange={setMode}
        options={[
          { value: "encode", label: strings.modes.encode },
          { value: "decode", label: strings.modes.decode },
        ]}
        value={mode}
      />
      {mode === "encode" ? (
        <ImageEncodePanel strings={strings} />
      ) : (
        <ImageDecodePanel strings={strings} />
      )}
    </section>
  );
}
